/**
 * EventTutoringSigningModal - signing modal for NON-SPONSORED event tutoring authorizations.
 *
 * Used on both sides of a tutoring/coaching event eBUCIS is not sponsoring:
 *   - side="participant": a registrant authorizes their participation.
 *   - side="host": the event host/tutor signs the host agreement.
 *
 * The server resolves the required role from the subject's age (see getParticipantReadiness /
 * getHostReadiness): an ADULT party self-signs; a MINOR (13-17) party cannot sign - their adult
 * guardian signs in their place; under-13 are blocked. This modal reads readiness, then either:
 *   - renders the role's document + ConsentForm (when the current user may sign), or
 *   - explains who must act (minor must wait for a guardian; a guardian must be linked first; or the
 *     party is too young / has no date of birth).
 * It enforces that ALL required authorizations are granted (no partial consent), mirroring the
 * server. Reuses ConsentForm (scroll-to-end + typed-name e-signature) and the existing tutoring
 * signing UX.
 */
import { useState, useEffect, useMemo } from 'react'
import { X, Loader2, AlertCircle, ShieldCheck, Info } from 'lucide-react'
import toast from 'react-hot-toast'
import ConsentForm from '../consent/ConsentForm'
import { useAuthStore } from '../../stores/authStore'
import eventTutoringService, {
  EVENT_TUTORING_SLUG_BY_ROLE,
  EVENT_TUTORING_ROLE_LABELS,
  EVENT_TUTORING_REQUIRED_CONSENTS_BY_ROLE
} from '../../services/eventTutoringService'

// Roles where the signer signs for themselves (vs. a guardian signing for a minor).
const SELF_ROLES = new Set(['participant', 'host'])

// Single source of truth for whether the current user may sign, derived from readiness. Used by both
// the document-fetch effect and the render so the two can never drift (fetch a doc that is never
// shown, or render ConsentForm with a null document).
function deriveSignState(readiness, effectiveSubjectId, userId) {
  const requiredRole = readiness?.requiredRole || null
  const isSelfRole = !!requiredRole && SELF_ROLES.has(requiredRole)
  const currentUserIsSubject = !!effectiveSubjectId && effectiveSubjectId === userId
  const guardianNotLinked = (readiness?.missing || []).some((m) => m.code === 'GUARDIAN_NOT_LINKED')
  // Self-roles: only the subject (an adult) may sign. Guardian roles: only someone who is NOT the
  // subject (the linked adult guardian) may sign. A missing guardian link blocks signing either way.
  const canSign =
    !!requiredRole &&
    (isSelfRole ? currentUserIsSubject : !currentUserIsSubject) &&
    !guardianNotLinked
  return { requiredRole, isSelfRole, currentUserIsSubject, guardianNotLinked, canSign }
}

export default function EventTutoringSigningModal({
  isOpen,
  eventSeriesId,
  side = 'participant',
  subjectId,
  subjectName,
  onClose,
  onSigned
}) {
  const { user } = useAuthStore()
  const [readiness, setReadiness] = useState(null)
  const [document, setDocument] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [processing, setProcessing] = useState(false)

  // The party the authorization is about. For an adult self-sign this is the current user.
  const effectiveSubjectId = subjectId || user?.id || null

  useEffect(() => {
    if (!isOpen || !eventSeriesId) return

    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError('')
      setReadiness(null)
      setDocument(null)
      try {
        const data =
          side === 'host'
            ? await eventTutoringService.getHostReadiness(eventSeriesId)
            : await eventTutoringService.getParticipantReadiness(eventSeriesId, subjectId)
        if (cancelled) return
        setReadiness(data)

        // Only fetch the document when the current user can actually sign it (same derivation the
        // render uses, so the fetch and the displayed form never disagree).
        const { requiredRole: role, canSign } = deriveSignState(data, effectiveSubjectId, user?.id)
        if (!data?.ready && canSign) {
          const doc = await eventTutoringService.getDocument(EVENT_TUTORING_SLUG_BY_ROLE[role])
          if (!cancelled) setDocument(doc)
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load the authorization')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [isOpen, eventSeriesId, side, subjectId, effectiveSubjectId, user?.id])

  const {
    requiredRole,
    isSelfRole,
    guardianNotLinked,
    canSign: canCurrentUserSign
  } = useMemo(
    () => deriveSignState(readiness, effectiveSubjectId, user?.id),
    [readiness, effectiveSubjectId, user?.id]
  )
  const requiredTypes = useMemo(
    () => (requiredRole ? EVENT_TUTORING_REQUIRED_CONSENTS_BY_ROLE[requiredRole] || [] : []),
    [requiredRole]
  )

  const handleSubmit = async ({ consentTypes, signatureName }) => {
    // No partial consent: every required authorization for the role must be granted.
    const grantedAll = requiredTypes.every((t) => consentTypes.includes(t))
    if (!grantedAll) {
      toast.error('Please authorize all listed items to continue.')
      return
    }

    setProcessing(true)
    try {
      await eventTutoringService.sign(eventSeriesId, {
        signerRole: requiredRole,
        // Guardian roles must name the minor subject; for a self-sign the server defaults to the signer.
        subjectId: isSelfRole ? undefined : effectiveSubjectId,
        consentTypes,
        signatureName
      })
      toast.success('Authorization recorded.')
      onSigned?.()
      onClose?.()
    } catch (err) {
      toast.error(err.message || 'Failed to record authorization')
    } finally {
      setProcessing(false)
    }
  }

  if (!isOpen) return null

  const heading =
    (requiredRole && EVENT_TUTORING_ROLE_LABELS[requiredRole]) ||
    (side === 'host' ? 'Host / tutor agreement' : 'Tutoring authorization')

  // The non-signable explanation (who must act), driven by readiness.
  const renderInfoState = () => {
    const labels = (readiness?.missing || []).map((m) => m.label).filter(Boolean)

    let message
    if (!requiredRole) {
      // Under-13 blocked or no date of birth on file.
      message =
        labels[0] || 'This person is not currently eligible to take part in tutoring sessions.'
    } else if (guardianNotLinked) {
      message =
        side === 'host'
          ? 'A parent or guardian must be linked to this account before a minor can host tutoring sessions.'
          : 'A parent or guardian must be linked to your account before they can authorize your participation. Add a guardian in your profile, then ask them to sign.'
    } else {
      // A guardian must sign, but the current user is the minor (not the guardian).
      message =
        side === 'host'
          ? 'A parent or guardian must authorize this host before the event can take place. They can sign from their own account.'
          : 'A parent or guardian must authorize your participation before you can join. They can sign from their own account.'
    }

    return (
      <div className="py-6 text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
          <Info className="w-7 h-7 text-amber-600 dark:text-amber-400" />
        </div>
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-4 max-w-md mx-auto">{message}</p>
        {labels.length > 1 && (
          <ul className="text-xs text-gray-500 dark:text-gray-400 mb-4 space-y-1">
            {labels.map((l, i) => (
              <li key={i}>• {l}</li>
            ))}
          </ul>
        )}
        <button
          onClick={onClose}
          className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium"
        >
          Close
        </button>
      </div>
    )
  }

  const renderAlreadyAuthorized = () => (
    <div className="py-8 text-center">
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
        <ShieldCheck className="w-7 h-7 text-green-600 dark:text-green-400" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
        Authorization complete
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
        {side === 'host'
          ? 'This event is authorized to take place.'
          : 'You are authorized to join this tutoring session.'}
      </p>
      <button
        onClick={onClose}
        className="px-5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium"
      >
        Close
      </button>
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{heading}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="p-5">
          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              Loading authorization…
            </div>
          )}

          {!loading && error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && readiness?.ready && renderAlreadyAuthorized()}

          {!loading && !error && !readiness?.ready && !canCurrentUserSign && renderInfoState()}

          {!loading && !error && !readiness?.ready && canCurrentUserSign && document && (
            <ConsentForm
              document={{ ...document, consentTypes: requiredTypes }}
              studentName={subjectName}
              onSubmit={handleSubmit}
              processing={processing}
              submitLabel="Sign authorization"
            />
          )}
        </div>
      </div>
    </div>
  )
}
